import { useCallback, useState } from 'react'
import { Archive, ArchiveRestore, ImageIcon, MoreHorizontal, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'

type TaskOptionsMenuProps = {
  taskId: string
  taskName?: string
  className?: string
  buttonClassName?: string
  isArchived?: boolean
  onDelete?: () => void
  onCoverImage?: () => void
  onArchive?: () => void
  onUnarchive?: () => void
  variant?: 'icon' | 'button'
}

export function TaskOptionsMenu({
  taskId,
  taskName = 'this task',
  className,
  buttonClassName,
  isArchived,
  onDelete,
  onCoverImage,
  onArchive,
  onUnarchive,
}: TaskOptionsMenuProps) {
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { deleteTask } = useTaskMutations()
  const canDeleteTasks = useHasPermission(WORKSPACE_PERMISSIONS.DELETE_TASKS)

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true)
      await deleteTask(taskId)
      setIsDeleteAlertOpen(false)
      onDelete?.()
    } catch (error) {
      logger.logError(
        error as Error,
        LogCategory.TASK_DELETE,
        {
          component: 'TaskOptionsMenu',
          taskId,
        }
      )
    } finally {
      setIsDeleting(false)
    }
  }, [taskId, deleteTask, onDelete])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent',
              buttonClassName,
            )}
            aria-label="More options"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={className}>
          {onCoverImage && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onCoverImage()
              }}
            >
              <ImageIcon className="size-4" />
              Cover image
            </DropdownMenuItem>
          )}
          {isArchived ? (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onUnarchive?.()
              }}
            >
              <ArchiveRestore className="size-4" />
              Unarchive task
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onArchive?.()
              }}
            >
              <Archive className="size-4" />
              Archive task
            </DropdownMenuItem>
          )}
          {canDeleteTasks && (
            <DropdownMenuItem
              variant="destructive"
              onClick={(e) => {
                e.stopPropagation()
                setIsDeleteAlertOpen(true)
              }}
            >
              <Trash2 className="size-4" />
              Delete task
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader className="overflow-hidden">
            <AlertDialogTitle className="truncate">
              Delete "{taskName}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This task and its associated data
              will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.stopPropagation()
                await handleDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
